import { useTranslation } from 'react-i18next'
import {
  CreatableMultiSelectFormField,
  MultiSelectFormField,
  SelectFormField,
} from '../../../../../common/components/Form/SelectFormField/SelectFormField'
import { TextAreaFormField } from '../../../../../common/components/Form/TextAreaFormField/TextAreaFormField'
import { EditorPermissions } from '../../../../../types/commonTypes'
import { getAgeOptions, getLanguageOptions, getSubjectOptions } from '../../../../search/components/constants'
import { GameForm } from '../../../types'
import { GAME_DESCRIPTION_MAX_LENGTH } from '../../QuickStartWizard/steps/GameInfoStep'
import styles from '../GameSettingsModal.module.css'
import { EditorFormField } from '../../../../../common/components/Form/EditorFormField/EditorFormField'

const shouldDisableField = (permissions: EditorPermissions, generalField: keyof GameForm) => {
  if (typeof permissions.gameSettings === 'boolean') {
    return !permissions.gameSettings
  }
  return !permissions.gameSettings?.[generalField]
}

type GameDetailsProps = {
  permissions: EditorPermissions
}

export const GameDetails: React.FC<GameDetailsProps> = ({ permissions }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.generalSection}>
      <TextAreaFormField
        name='description'
        label={t('game_editor.game_settings.general.description_label', 'Description')}
        placeholder={t(
          'game_editor.game_settings.general.description_placeholder',
          'Write a general description of the game',
        )}
        fieldContainerClassName={styles.descriptionFieldContainer}
        className={styles.descriptionField}
        maxLength={GAME_DESCRIPTION_MAX_LENGTH}
        disabled={shouldDisableField(permissions, 'description')}
      />
      <EditorFormField
        name='instructionsForTeacherHtml'
        label={t('game_editor.game_settings.general.instructions_for_techer_label', 'Guidelines for instructors ')}
        placeholder={t(
          'game_editor.game_settings.general.instructions_for_techer_placeholder',
          'Guidelines for instructors about playing and grading the game',
        )}
        onlyBasicOptions
        initProps={{ height: 200, width: '100%' }}
        validateOnlyIfDirty
        disabled={shouldDisableField(permissions, 'instructionsForTeacherHtml')}
      />
      <div className={styles.dropdownsContainer}>
        <MultiSelectFormField
          name='topics'
          label={t('game_editor.game_settings.general.topics_label', 'Topics')}
          placeholder={t('game_editor.game_settings.general.topics_placeholder', 'Select topics')}
          options={getSubjectOptions(t)}
          isSearchable
          disabled={shouldDisableField(permissions, 'topics')}
        />
        <SelectFormField
          name='language'
          label={t('game_editor.game_settings.general.language_label', 'Language')}
          placeholder={t('game_editor.game_settings.general.language_placeholder', 'Select language')}
          options={getLanguageOptions(t)}
          isSearchable
          isClearable
          disabled={shouldDisableField(permissions, 'language')}
        />
      </div>
      <div className={styles.dropdownsContainer}>
        <CreatableMultiSelectFormField
          label={t('game_editor.game_settings.general.keywords_label', 'Keywords')}
          name='keywords'
          placeholder={t('game_editor.game_settings.general.keywords_placeholder', 'Enter keywords')}
          disabled={shouldDisableField(permissions, 'keywords')}
        />
        <MultiSelectFormField
          name='ages'
          label={t('game_editor.game_settings.general.ages_label', 'Ages')}
          placeholder={t('game_editor.game_settings.general.ages_placeholder', 'Select age')}
          options={getAgeOptions(t)}
          disabled={shouldDisableField(permissions, 'ages')}
        />
      </div>
    </div>
  )
}
